import { useState, useEffect } from 'react';
import JSZip from 'jszip';
import { Download, Image as ImageIcon } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Input';
import { Slider } from '@/components/ui/Slider';
import { Select } from '@/components/ui/Select';
import { Dropzone } from '@/components/ui/Dropzone';
import { Badge } from '@/components/ui/Badge';
import { useApp } from '@/store/app';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { readFileAsArrayBuffer } from '@/lib/utils';
import { pdfjs } from '@/lib/pdfjs';

interface PageImage {
  page: number;
  url: string;
  blob: Blob;
}

export function PdfToImages() {
  const { pendingFile, setPendingFile } = useApp();
  const [file, setFile] = useState<File | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [scale, setScale] = useState(2);
  const [format, setFormat] = useState<'png' | 'jpeg' | 'webp'>('png');
  const [pages, setPages] = useState<PageImage[]>([]);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (pendingFile && pendingFile.type === 'application/pdf') {
      handleFile([pendingFile]);
      setPendingFile(null);
    }
    return () => {
      pages.forEach((p) => URL.revokeObjectURL(p.url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFile(files: File[]) {
    const f = files[0];
    if (!f) return;
    setFile(f);
    setPages([]);
    const buf = await readFileAsArrayBuffer(f);
    const doc = await pdfjs.getDocument({ data: buf }).promise;
    setPageCount(doc.numPages);
  }

  async function render() {
    if (!file) return;
    setWorking(true);
    pages.forEach((p) => URL.revokeObjectURL(p.url));
    try {
      const buf = await readFileAsArrayBuffer(file);
      const doc = await pdfjs.getDocument({ data: buf }).promise;
      const out: PageImage[] = [];
      for (let i = 1; i <= doc.numPages; i++) {
        const page = await doc.getPage(i);
        const viewport = page.getViewport({ scale });
        const c = document.createElement('canvas');
        c.width = viewport.width;
        c.height = viewport.height;
        const ctx = c.getContext('2d')!;
        await page.render({ canvasContext: ctx, viewport, canvas: c }).promise;
        const blob: Blob = await new Promise((resolve) =>
          c.toBlob((b) => resolve(b!), `image/${format}`, 0.92),
        );
        out.push({ page: i, blob, url: URL.createObjectURL(blob) });
      }
      setPages(out);
      toast.success(`Rendered ${out.length} pages`);
    } catch (e) {
      toast.error('Render failed');
    } finally {
      setWorking(false);
    }
  }

  async function downloadAll() {
    if (!pages.length) return;
    const zip = new JSZip();
    const ext = format === 'jpeg' ? 'jpg' : format;
    for (const p of pages) {
      const buf = await p.blob.arrayBuffer();
      zip.file(`page-${String(p.page).padStart(3, '0')}.${ext}`, buf);
    }
    const blob = await zip.generateAsync({ type: 'blob' });
    saveAs(blob, file!.name.replace(/\.pdf$/i, '') + '-images.zip');
    toast.success('ZIP downloaded');
  }

  return (
    <div>
      <ToolHeader
        toolId="pdf-to-images"
        title="PDF → Images"
        description="Render every page to a PNG, JPEG, or WebP image. Bulk-download as a ZIP."
        badges={pageCount ? <Badge>{pageCount} pages</Badge> : null}
      />

      {!file ? (
        <Dropzone accept="application/pdf" onFiles={handleFile} />
      ) : (
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <Card className="lg:col-span-2 flex items-center justify-between">
              <div className="text-sm font-medium truncate">{file.name}</div>
              <Button size="sm" variant="ghost" onClick={() => setFile(null)}>Change</Button>
            </Card>
            <Card className="space-y-3">
              <Field label="Resolution" hint={`${scale}× · DPI ~${Math.round(72 * scale)}`}>
                <Slider value={scale} onChange={setScale} min={1} max={4} step={0.5} showValue={false} />
              </Field>
              <Field label="Format">
                <Select
                  value={format}
                  onChange={(e) => setFormat(e.target.value as typeof format)}
                  options={[
                    { value: 'png', label: 'PNG · lossless' },
                    { value: 'jpeg', label: 'JPEG · smaller' },
                    { value: 'webp', label: 'WebP · modern' },
                  ]}
                />
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  loading={working}
                  disabled={working}
                  onClick={render}
                >
                  <ImageIcon size={11} />
                  Render
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={downloadAll}
                  disabled={!pages.length}
                >
                  <Download size={11} />
                  Save ZIP
                </Button>
              </div>
            </Card>
          </div>

          {pages.length > 0 && (
            <Card className="!p-0 overflow-hidden">
              <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
                {pages.length} pages
              </div>
              <div className="p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 max-h-[600px] overflow-y-auto">
                {pages.map((p) => (
                  <div
                    key={p.page}
                    className="group relative rounded-lg overflow-hidden border border-border bg-bg-inset"
                  >
                    <img
                      src={p.url}
                      alt={`page ${p.page}`}
                      className="w-full h-auto block"
                    />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 flex items-center justify-between opacity-0 group-hover:opacity-100 transition">
                      <span className="text-xs text-white font-mono">page {p.page}</span>
                      <button
                        onClick={() => {
                          const ext = format === 'jpeg' ? 'jpg' : format;
                          saveAs(p.blob, `page-${p.page}.${ext}`);
                        }}
                        className="size-6 rounded bg-white/20 hover:bg-white/30 flex items-center justify-center text-white"
                      >
                        <Download size={10} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

export default PdfToImages;
